import React, { useEffect, useMemo, useRef, useState } from "react";
import { MicrophoneIcon, StopIcon } from "@heroicons/react/20/solid";

const SpeechToText = () => {
  const [text, setText] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [updateMeeting, setUpdateMeeting] = useState("");
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [permissionError, setPermissionError] = useState("");
  const [autoSavedAt, setAutoSavedAt] = useState(null);
  const [restoredDraft, setRestoredDraft] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState("");
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [awaitingUserResume, setAwaitingUserResume] = useState(false);

  const startAudioRef = useRef(null);
  const stopAudioRef = useRef(null);
  const recognitionRef = useRef(null);
  const shouldListenRef = useRef(false);
  const isMountedRef = useRef(true);
  const restartTimerRef = useRef(null);
  const recordingStartedAtRef = useRef(null);
  const durationTimerRef = useRef(null);
  const titleFlashIntervalRef = useRef(null);
  const originalTitleRef = useRef(document.title);

  const meetingId = window.location.pathname.split("/").pop();
  const storageKey = useMemo(() => `notetaker:draft:${meetingId}`, [meetingId]);

  const persistDraft = (next = {}) => {
    const payload = {
      text,
      shouldResume: shouldListenRef.current,
      updatedAt: Date.now(),
      ...next,
    };
    localStorage.setItem(storageKey, JSON.stringify(payload));
    setAutoSavedAt(payload.updatedAt);
  };

  const clearDraft = () => {
    localStorage.removeItem(storageKey);
    setAutoSavedAt(null);
    setRestoredDraft(false);
  };

  const requestMicrophonePermission = async () => {
    try {
      if (!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)) {
        throw new Error("getUserMedia not supported");
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      setPermissionGranted(true);
      setPermissionError("");
      return true;
    } catch (error) {
      console.error("Microphone permission denied:", error);
      setPermissionGranted(false);
      setPermissionError(
        "Microphone permission is required. Please allow microphone access and try again."
      );
      return false;
    }
  };

  const startDurationTimer = () => {
    if (durationTimerRef.current) clearInterval(durationTimerRef.current);
    recordingStartedAtRef.current = Date.now();
    setRecordingDuration(0);

    durationTimerRef.current = setInterval(() => {
      if (!recordingStartedAtRef.current) return;
      const seconds = Math.floor((Date.now() - recordingStartedAtRef.current) / 1000);
      setRecordingDuration(seconds);
    }, 1000);
  };

  const stopDurationTimer = () => {
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
    recordingStartedAtRef.current = null;
    setRecordingDuration(0);
  };

  const stopRecognition = (persistState = true) => {
    shouldListenRef.current = false;

    if (restartTimerRef.current) {
      clearTimeout(restartTimerRef.current);
      restartTimerRef.current = null;
    }

    if (recognitionRef.current) {
      try {
        recognitionRef.current.onend = null;
        recognitionRef.current.stop();
      } catch (_error) {
        // No-op
      }
      recognitionRef.current = null;
    }

    setIsListening(false);
    setInterimTranscript("");
    setAwaitingUserResume(false);
    stopDurationTimer();

    if (persistState) {
      persistDraft({ shouldResume: false });
    }

    if (stopAudioRef.current) stopAudioRef.current.play();
  };

  const requestUserResume = (reason = "Recording paused. Move mouse, click, or press any key to continue.") => {
    setAwaitingUserResume(true);
    setPermissionError(reason);
  };

  const startRecognition = async () => {
    if (!permissionGranted) {
      const granted = await requestMicrophonePermission();
      if (!granted) {
        stopRecognition();
        return;
      }
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Speech Recognition is not supported in your browser.");
      return;
    }

    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (_error) {
        // No-op
      }
      recognitionRef.current = null;
    }

    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onresult = (event) => {
      let nextInterim = "";
      let finalChunk = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalChunk += transcript + " ";
        } else {
          nextInterim += transcript;
        }
      }

      if (finalChunk) {
        setText((prev) => {
          const updated = prev + finalChunk;
          localStorage.setItem(
            storageKey,
            JSON.stringify({ text: updated, shouldResume: true, updatedAt: Date.now() })
          );
          setAutoSavedAt(Date.now());
          return updated;
        });
      }

      setInterimTranscript(nextInterim);
    };

    recognition.onerror = (event) => {
      setPermissionError("Speech recognition error: " + event.error);
      setIsListening(false);
      if (event.error === "not-allowed" || event.error === "service-not-allowed") {
        shouldListenRef.current = false;
      }
    };

    recognition.onend = () => {
      recognitionRef.current = null;
      if (!isMountedRef.current) return;

      if (shouldListenRef.current) {
        restartTimerRef.current = setTimeout(() => {
          if (shouldListenRef.current) {
            startRecognition();
          }
        }, 500);
      } else {
        setIsListening(false);
        setInterimTranscript("");
        stopDurationTimer();
        if (stopAudioRef.current) stopAudioRef.current.play();
      }
    };

    try {
      recognition.start();
      shouldListenRef.current = true;
      setIsListening(true);
      setAwaitingUserResume(false);
      setPermissionError("");
      startDurationTimer();
      persistDraft({ shouldResume: true });
      if (startAudioRef.current) startAudioRef.current.play();
    } catch (error) {
      const autoResumeIntent = shouldListenRef.current;
      setIsListening(false);
      stopDurationTimer();

      if (autoResumeIntent) {
        requestUserResume("Browser paused recording. Move mouse, click, or press any key to resume.");
        persistDraft({ shouldResume: true });
      } else {
        setPermissionError("Unable to start speech recognition. Please try again.");
        shouldListenRef.current = false;
        persistDraft({ shouldResume: false });
      }
    }
  };

  const handleListenToggle = async () => {
    if (isListening) {
      stopRecognition();
      return;
    }
    await startRecognition();
  };

  const handleSaveText = async () => {
    if (!text.trim()) {
      alert("No text available to save!");
      return;
    }

    try {
      const payload = {
        id: meetingId,
        MeetingNotes: text.trim(),
      };

      const response = await fetch("https://api.insightos.co.uk/api/savemeetingnotes", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to save meeting notes");
      }

      setUpdateMeeting("Meeting notes saved successfully.");
      clearDraft();
      shouldListenRef.current = false;

      setTimeout(() => {
        window.close();
      }, 2000);
    } catch (error) {
      console.error("Error saving meeting notes:", error);
      setPermissionError("Unable to save to server. Local draft remains available.");
    }
  };

  const handleReset = () => {
    setText("");
    setInterimTranscript("");
    clearDraft();
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      persistDraft();
    }, 400);
    return () => clearTimeout(timer);
  }, [text, storageKey]);

  useEffect(() => {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return;

    try {
      const parsed = JSON.parse(raw);
      if (parsed?.text) {
        setText(parsed.text);
        setRestoredDraft(true);
      }
      if (parsed?.updatedAt) {
        setAutoSavedAt(parsed.updatedAt);
      }
      if (parsed?.shouldResume) {
        shouldListenRef.current = true;
        setTimeout(() => {
          startRecognition();
        }, 350);
      }
    } catch (_error) {
      // Ignore corrupted draft data.
    }
  }, [storageKey]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      persistDraft({ shouldResume: shouldListenRef.current });
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [text, storageKey]);

  useEffect(() => {
    if (!awaitingUserResume || isListening) return;

    const tryResume = () => {
      if (!shouldListenRef.current) return;
      setAwaitingUserResume(false);
      startRecognition();
    };

    const events = ["mousemove", "pointerdown", "keydown", "touchstart", "focus"];
    events.forEach((eventName) => {
      window.addEventListener(eventName, tryResume, { passive: true });
    });

    return () => {
      events.forEach((eventName) => {
        window.removeEventListener(eventName, tryResume);
      });
    };
  }, [awaitingUserResume, isListening]);

  useEffect(() => {
    if (!awaitingUserResume) {
      if (titleFlashIntervalRef.current) {
        clearInterval(titleFlashIntervalRef.current);
        titleFlashIntervalRef.current = null;
      }
      document.title = originalTitleRef.current;
      return;
    }

    let toggle = false;
    titleFlashIntervalRef.current = setInterval(() => {
      toggle = !toggle;
      document.title = toggle ? "Resume Recording Required" : "Interview Note Taker";
    }, 700);

    return () => {
      if (titleFlashIntervalRef.current) {
        clearInterval(titleFlashIntervalRef.current);
        titleFlashIntervalRef.current = null;
      }
      document.title = originalTitleRef.current;
    };
  }, [awaitingUserResume]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      stopRecognition(false);
      if (durationTimerRef.current) clearInterval(durationTimerRef.current);
      if (restartTimerRef.current) clearTimeout(restartTimerRef.current);
    };
  }, []);

  const prettySavedAt = useMemo(() => {
    if (!autoSavedAt) return "Not saved yet";
    return `Saved ${new Date(autoSavedAt).toLocaleTimeString()}`;
  }, [autoSavedAt]);

  const liveTranscript = `${text}${interimTranscript}`.trim();

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-100 via-sky-50 to-emerald-50 px-4 py-6 md:px-8 md:py-10">
      <audio ref={startAudioRef} src="/pop.mp3" preload="auto" />
      <audio ref={stopAudioRef} src="/woosh.mp3" preload="auto" />

      <div className="mx-auto w-full max-w-4xl rounded-3xl border border-indigo-200/70 bg-white/90 p-5 shadow-[0_24px_60px_rgba(79,70,229,0.16)] backdrop-blur-xl md:p-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-indigo-200/60 bg-gradient-to-r from-indigo-100/80 via-white/85 to-sky-100/80 p-4">
          <div className="flex items-center gap-3">
            <img src="/2.png" alt="Note Taker" className="h-10 w-10 rounded-xl border border-indigo-200/60 bg-white p-1" />
            <div>
              <h3 className="text-lg font-bold text-indigo-900">Interview Note Taker</h3>
              <p className="text-xs font-medium text-indigo-700">
                Meeting ID: <span className="font-semibold">{meetingId || "N/A"}</span>
              </p>
            </div>
          </div>
          <button
            onClick={() => window.close()}
            className="rounded-xl bg-gradient-to-r from-rose-600 to-pink-600 px-4 py-2 text-sm font-semibold text-white shadow-md hover:from-rose-500 hover:to-pink-500"
          >
            Close
          </button>
        </div>

        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="rounded-2xl border border-indigo-200/60 bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">Recorder</p>
            <p className="mt-1 text-sm font-bold text-slate-900">{isListening ? "Listening" : "Idle"}</p>
          </div>
          <div className="rounded-2xl border border-sky-200/60 bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-sky-700">Duration</p>
            <p className="mt-1 text-sm font-bold text-slate-900">
              {Math.floor(recordingDuration / 60)}:{("0" + (recordingDuration % 60)).slice(-2)}
            </p>
          </div>
          <div className="rounded-2xl border border-emerald-200/60 bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Local Draft</p>
            <p className="mt-1 text-sm font-bold text-slate-900">{prettySavedAt}</p>
          </div>
        </div>

        <div className="mb-6 flex flex-wrap items-center gap-3">
          <button
            onClick={handleListenToggle}
            className={`inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold text-white shadow-md transition ${
              isListening
                ? "bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-500 hover:to-pink-500"
                : "bg-gradient-to-r from-indigo-600 to-sky-600 hover:from-indigo-500 hover:to-sky-500"
            }`}
          >
            <MicrophoneIcon className="h-5 w-5" />
            {isListening ? "Stop Recording" : "Start Recording"}
          </button>

          <button
            onClick={() => stopRecognition()}
            disabled={!isListening}
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-slate-600 to-slate-700 px-5 py-3 text-sm font-semibold text-white shadow-md hover:from-slate-500 hover:to-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <StopIcon className="h-5 w-5" />
            Force Stop
          </button>
        </div>

        {awaitingUserResume && (
          <div className="mb-6 rounded-xl border border-amber-300/70 bg-amber-50 p-3 text-sm font-semibold text-amber-800">
            Recording paused by browser policy. Move your mouse, click anywhere, or press any key to resume.
          </div>
        )}

        <div className="mb-6 rounded-2xl border border-indigo-200/60 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-indigo-700">Live Transcript</h2>
            {restoredDraft ? (
              <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">
                Recovered after refresh
              </span>
            ) : null}
          </div>
          <div className="max-h-[360px] min-h-[180px] overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-800">
              {liveTranscript || <span className="italic text-slate-400">Awaiting input...</span>}
            </p>
          </div>
        </div>

        <div className="mb-2 flex flex-wrap gap-3">
          <button
            onClick={handleSaveText}
            data-component="meeting-note-recorder-browser"
            data-time-saved="60"
            data-feature="save_meeting_notes_put"
            className="flex-1 rounded-xl bg-gradient-to-r from-emerald-600 to-green-600 px-5 py-3 text-sm font-semibold text-white shadow-md hover:from-emerald-500 hover:to-green-500"
          >
            Save To Meeting
          </button>
          <button
            onClick={handleReset}
            className="flex-1 rounded-xl bg-gradient-to-r from-sky-600 to-indigo-600 px-5 py-3 text-sm font-semibold text-white shadow-md hover:from-sky-500 hover:to-indigo-500"
          >
            Reset Local Draft
          </button>
        </div>

        {updateMeeting && (
          <div className="mt-4 rounded-xl border border-emerald-300/60 bg-emerald-50 p-3 text-center text-sm font-semibold text-emerald-800">
            {updateMeeting}
          </div>
        )}

        {permissionError && (
          <div className="mt-4 rounded-xl border border-rose-300/60 bg-rose-50 p-3 text-center text-sm font-semibold text-rose-800">
            {permissionError}
          </div>
        )}
      </div>
    </div>
  );
};

export default SpeechToText;
